// SEQ-180 phase 2, plan decision D20: comparison and widget data are fetched
// per distinct REQUEST KEY, never per widget. A dashboard's widgets are
// grouped by (dataGrain, resolved range, filter signature), the metrics each
// widget needs are unioned inside that group, and one RPC call is issued per
// distinct key. The 13 widget default dashboard resolves to two keys, which
// is exactly the two standing calls the phase 1 page already made
// (features/pnlAnalytics/usePnlAnalytics.ts), so phase 2 adds no network cost
// to the page a user who never opens the builder sees.
//
// A hard cap of eight distinct keys per render stops a pathological dashboard
// firing twenty calls. Over the cap, the keys are kept in first-appearance
// order and every widget that lost its data is named, so the page can say
// which widgets went unloaded instead of quietly showing them empty.
//
// Pure module, no React and no supabase import, so it can be proven under
// Deno (dashboardRequestKeys.test.ts) the same way filterState.ts and
// fiscalYear.ts are.
//
// DATE RANGE PRECEDENCE. The page's sticky filter bar is the dashboard's
// range, and every widget follows it by default. A widget pins its own range
// only when its config says so explicitly (`dateRangeMode: "widget"`), which
// is what the editor's "Date range" control writes when the user picks
// anything other than "Follow the dashboard filter". Without that rule the
// filter bar's date control would be dead on the default dashboard, because
// all 13 of its configs name a preset of their own.

import { datePreset, comparisonPeriod, type DateRange } from "../../lib/pnl/fiscalYear";
import type { PnlWidgetConfig } from "../../lib/pnl/pnlWidgetSchema";
import { resolveAggregationMethod, type PnlMetricDefinition } from "../../lib/pnl/pnlAnalytics";
import { PNL_ROLLUP_METRICS } from "./rollup";
import { capMonthOnMonthMetrics } from "./monthOnMonth";
import { ROLLUP_BINDING_BY_METRIC } from "./widgetPresentation";

/** D20: the ceiling on distinct RPC calls one render of one dashboard may issue. */
export const MAX_REQUEST_KEYS = 8;

/** A dashboard-scoped named range (plan decision D19), stored on `pnl_dashboards.date_presets`. */
export interface PnlCustomDatePreset {
  id: string;
  name: string;
  from: string;
  to: string;
}

/** The page-level filters every widget inherits: the sticky filter bar's own state, already resolved. */
export interface PnlDashboardPageFilters {
  range: DateRange;
  projectIds: string[];
  statuses: string[];
}

/** One RPC call: a grain, a range, a filter set and the union of every metric the widgets in this group need. */
export interface PnlRequestKey {
  key: string;
  groupBy: string;
  from: string | null;
  to: string | null;
  projectIds: string[];
  statuses: string[];
  metrics: string[];
  /** True when this key exists only to satisfy a widget's `comparison` (plan section 6, D20). */
  isComparison: boolean;
}

export interface PnlRequestPlan {
  keys: PnlRequestKey[];
  /** The key each widget reads its current-period data from, or null when the cap dropped it. */
  keyByWidgetId: Record<string, string | null>;
  /** The key each widget reads its comparison data from. Absent when the widget has no comparison. */
  comparisonKeyByWidgetId: Record<string, string | null>;
  /** True when more than MAX_REQUEST_KEYS distinct keys were needed. */
  capExceeded: boolean;
  /** Widgets left without data because the cap was reached, named so the page can say so. */
  droppedWidgetIds: string[];
}

/**
 * The RPC grain a widget fetches. `dataGrain` wins when the config sets it
 * (D16: the builder's one "Group by" control writes `dataGrain` and derives
 * `groupBy`). Otherwise the client rollup grain is mapped to the nearest
 * grain the RPC can actually serve: `company` and `project_month` are client
 * concepts, so both fetch at `project` grain and are rolled up here, which is
 * exactly what the phase 1 page already does for its KPI cards.
 *
 * Code review H2/H3 (D17): that project-grain fetch is only correct for a
 * metric the client can SUM. A company total of an avg, min, max, latest or
 * calculated metric cannot be recovered from per-project rows (a mean of
 * project means is not the mean; a sum of project snapshots mixes dates;
 * a ratio of ratios is the AC10 mistake), so a `company` widget for such a
 * metric fetches at `company` grain and reads the RPC's single row. The
 * registry decides which metrics those are; without a registry the phase 1
 * behaviour stands, which is safe because every phase 1 widget either sums
 * or reads a rollup binding. A metric WITH a rollup binding
 * (ROLLUP_BINDING_BY_METRIC, the eight phase 1 KPI cards) always stays on
 * the project-grain fetch whatever its registry method: its card reads the
 * client-side sum-then-divide rollup, never foldMetric, and moving it to a
 * server-side company row would change the default dashboard's numbers
 * that P14 keeps pixel-identical.
 */
export function resolveDataGrain(
  config: Pick<PnlWidgetConfig, "groupBy" | "dataGrain"> & { metric?: string; chartType?: PnlWidgetConfig["chartType"] },
  registry?: readonly PnlMetricDefinition[],
): string {
  if (typeof config.dataGrain === "string" && config.dataGrain.length > 0) return config.dataGrain;
  // SEQ-193: a month_on_month widget's columns are calendar months, so it
  // always fetches at month grain regardless of its `groupBy` (which here
  // means "rows are metrics" vs "rows are projects", not a fetch grain). The
  // editor also writes `dataGrain: "month"` directly (belt and suspenders,
  // matching this module's own rule that an explicit `dataGrain` always
  // wins), so this only matters for a config missing it.
  if (config.chartType === "month_on_month") return "month";
  if (config.groupBy === "company") {
    if (config.metric !== undefined && config.metric in ROLLUP_BINDING_BY_METRIC) return "project";
    const def = registry?.find((d) => d.metricKey === config.metric);
    if (def && resolveAggregationMethod(def) !== "sum") return "company";
    return "project";
  }
  if (config.groupBy === "project_month") return "project";
  return config.groupBy;
}

/**
 * The range a widget actually asks for. Follows the page filter unless the
 * config pins its own (see the file header). A dashboard custom preset id
 * resolves against `presets`; an id that no longer exists falls back to the
 * page range rather than throwing, because deleting a preset must not blank
 * every widget that referenced it.
 */
export function resolveWidgetRange(
  config: PnlWidgetConfig,
  page: PnlDashboardPageFilters,
  presets: PnlCustomDatePreset[],
  todayIso: string,
): DateRange {
  if (config.dateRangeMode !== "widget") return page.range;

  const customPresetId = config.dateRange.customPresetId;
  if (typeof customPresetId === "string") {
    const preset = presets.find((p) => p.id === customPresetId);
    if (!preset) return page.range;
    return { from: preset.from, to: preset.to };
  }

  if (config.dateRange.preset === "custom") {
    return config.dateRange.custom ?? page.range;
  }
  return datePreset(config.dateRange.preset, todayIso);
}

/**
 * Every metric one widget's rendering needs. The page's established rollup
 * set is always included: the phase 1 components this renderer reuses read
 * whole `PnlRollup` objects (revenue, cost, profit, overtime, patching,
 * hours), not one metric each, and the RPC pivots input metrics generically
 * from the registry, so asking for the set costs one column per key rather
 * than one call per widget.
 */
export function metricsForWidget(config: PnlWidgetConfig): string[] {
  const metrics = new Set<string>(PNL_ROLLUP_METRICS);
  metrics.add(config.metric);
  if (config.secondaryMetric) metrics.add(config.secondaryMetric);
  if (config.sort?.by) metrics.add(config.sort.by);
  // SEQ-193: a month_on_month widget in "company" groupBy mode can name
  // several metrics as rows (`metrics`), every one of which the widget's own
  // request key must fetch, not just the single `metric` field it falls
  // back to when unset.
  if (config.metrics) {
    // Month on month draws at most MONTH_ON_MONTH_MAX_METRICS rows, so it
    // fetches at most that many; other chart types keep every metric named.
    const named = config.chartType === "month_on_month" ? capMonthOnMonthMetrics(config.metrics) : config.metrics;
    for (const key of named) metrics.add(key);
  }
  return [...metrics];
}

function signatureOf(groupBy: string, range: DateRange, projectIds: string[], statuses: string[], isComparison: boolean): string {
  return JSON.stringify([
    groupBy,
    range.from,
    range.to,
    [...projectIds].sort(),
    [...statuses].sort(),
    isComparison,
  ]);
}

/** The project and status filters a widget resolves to: the page's, narrowed by the widget's own `filters` (never widened). */
export function resolveWidgetFilters(
  config: PnlWidgetConfig,
  page: PnlDashboardPageFilters,
): { projectIds: string[]; statuses: string[] } {
  let projectIds = page.projectIds;
  let statuses = page.statuses;

  for (const filter of config.filters) {
    const values = Array.isArray(filter.value) ? filter.value.map(String) : [String(filter.value)];
    if (filter.field === "projectId" || filter.field === "project_id") {
      projectIds = projectIds.length === 0 ? values : projectIds.filter((id) => values.includes(id));
    }
    if (filter.field === "status") {
      statuses = statuses.length === 0 ? values : statuses.filter((s) => values.includes(s));
    }
  }

  return { projectIds, statuses };
}

/**
 * Groups a dashboard's widgets into the smallest set of RPC calls that serves
 * all of them, current period and comparison period alike, and applies the
 * D20 cap. Deterministic: the same widgets in the same order always produce
 * the same keys in the same order, which is what lets the caller cache on the
 * key string.
 */
export function buildPnlRequestPlan(
  widgets: PnlWidgetConfig[],
  page: PnlDashboardPageFilters,
  presets: PnlCustomDatePreset[],
  todayIso: string,
  registry?: readonly PnlMetricDefinition[],
): PnlRequestPlan {
  const byKey = new Map<string, PnlRequestKey>();
  const keyByWidgetId: Record<string, string | null> = {};
  const comparisonKeyByWidgetId: Record<string, string | null> = {};
  const droppedWidgetIds: string[] = [];
  let capExceeded = false;

  const claim = (
    groupBy: string,
    range: DateRange,
    projectIds: string[],
    statuses: string[],
    metrics: string[],
    isComparison: boolean,
  ): string | null => {
    const key = signatureOf(groupBy, range, projectIds, statuses, isComparison);
    const existing = byKey.get(key);
    if (existing) {
      for (const metric of metrics) {
        if (!existing.metrics.includes(metric)) existing.metrics.push(metric);
      }
      return key;
    }
    if (byKey.size >= MAX_REQUEST_KEYS) {
      capExceeded = true;
      return null;
    }
    byKey.set(key, {
      key,
      groupBy,
      from: range.from,
      to: range.to,
      projectIds: [...projectIds],
      statuses: [...statuses],
      metrics: [...metrics],
      isComparison,
    });
    return key;
  };

  for (const config of widgets) {
    const grain = resolveDataGrain(config, registry);
    const range = resolveWidgetRange(config, page, presets, todayIso);
    const { projectIds, statuses } = resolveWidgetFilters(config, page);
    const metrics = metricsForWidget(config);

    const currentKey = claim(grain, range, projectIds, statuses, metrics, false);
    keyByWidgetId[config.widgetId] = currentKey;
    if (currentKey === null) droppedWidgetIds.push(config.widgetId);

    if (config.comparison) {
      const priorRange = comparisonPeriod(range, config.comparison.kind);
      // A lifetime range has no prior period (fiscalYear.ts returns both
      // bounds null). Asking for it would fetch the whole history again and
      // render a delta against itself, so it is not requested at all; the
      // editor blocks the pairing with the section 7.3 message.
      if (priorRange.from === null || priorRange.to === null) {
        comparisonKeyByWidgetId[config.widgetId] = null;
      } else {
        comparisonKeyByWidgetId[config.widgetId] = claim(grain, priorRange, projectIds, statuses, metrics, true);
      }
    }
  }

  return {
    keys: [...byKey.values()],
    keyByWidgetId,
    comparisonKeyByWidgetId,
    capExceeded,
    droppedWidgetIds,
  };
}
